import React, { useState, useEffect } from 'react';
import { getSettings, saveSettings as saveSettingsUtil, PVQSettings } from '../utils/settings';

const PVQPOCSettings: React.FC = () => {
  const [settings, setSettings] = useState<PVQSettings>(getSettings());
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Load settings from localStorage and fix invalid FAQ mode states
  useEffect(() => {
    const currentSettings = getSettings();

    // Check if we have an invalid state where both FAQ flags are true
    if (currentSettings.useKnowledgeBase && currentSettings.useAdvancedRAG) {
      console.log('Fixing invalid FAQ mode state: both flags were true, defaulting to Local RAG');
      const fixedSettings = {
        ...currentSettings,
        useKnowledgeBase: false,
        useAdvancedRAG: true
      };
      setSettings(fixedSettings);
      saveSettingsUtil(fixedSettings);
    } else {
      setSettings(currentSettings);
    }
  }, []);

  const handleSettingChange = (key: keyof PVQSettings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    saveSettingsAndNotify(newSettings);
  };

  const saveSettingsAndNotify = (newSettings: PVQSettings) => {
    setSaveStatus('saving');
    saveSettingsUtil(newSettings);

    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('pvq-settings-changed'));

    // Simulate save delay for better UX
    setTimeout(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 500);
  };

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">PVQ POC Settings</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Proof of Concept Settings:</strong> Configure AI assistance behavior, validation preferences, and knowledge base options for PVQ.ai POC components
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Auto-Check Field Completeness */}
            <div className="border-b border-gray-200 pb-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 mr-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Auto-Check Field Completeness
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Automatically check field completeness with AI validation when you stop typing in longer form fields.
                    If the AI determines the content is unsatisfactory, it will engage the AI Assist overlay to help improve the response.
                  </p>
                  <div className="flex items-center text-xs text-gray-500">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Checks trigger after 3 seconds of inactivity
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settings.autoCheckFieldCompleteness}
                        onChange={(e) => handleSettingChange('autoCheckFieldCompleteness', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`block w-14 h-8 rounded-full transition-colors duration-200 ${
                        settings.autoCheckFieldCompleteness ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-200 ${
                          settings.autoCheckFieldCompleteness ? 'transform translate-x-6' : ''
                        }`}></div>
                      </div>
                    </div>
                  </label>
                  <span className="text-xs text-gray-500 mt-1">
                    {settings.autoCheckFieldCompleteness ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {/* AI Validation Threshold */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                AI Validation Threshold
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Set how strict the AI validation should be when checking field completeness.
              </p>
              <div className="space-y-3">
                {[
                  { value: 'low', label: 'Low', description: 'Basic completeness check - ensures minimum content requirements' },
                  { value: 'medium', label: 'Medium', description: 'Balanced validation - checks completeness and basic quality' },
                  { value: 'high', label: 'High', description: 'Strict validation - comprehensive quality and completeness check' }
                ].map((option) => (
                  <label key={option.value} className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      name="aiValidationThreshold"
                      value={option.value}
                      checked={settings.aiValidationThreshold === option.value}
                      onChange={(e) => handleSettingChange('aiValidationThreshold', e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-0.5"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">{option.label}</span>
                      <p className="text-xs text-gray-500">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Encouraging Feedback */}
            <div className="border-b border-gray-200 pb-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 mr-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Encouraging Feedback
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Show animated "Looks good!" indicators and positive feedback when AI determines field answers are satisfactory.
                  </p>
                  <div className="flex items-center text-xs text-gray-500">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Provides positive reinforcement for quality responses
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settings.encouragingFeedback}
                        onChange={(e) => handleSettingChange('encouragingFeedback', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`block w-14 h-8 rounded-full transition-colors duration-200 ${
                        settings.encouragingFeedback ? 'bg-green-600' : 'bg-gray-300'
                      }`}>
                        <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-200 ${
                          settings.encouragingFeedback ? 'transform translate-x-6' : ''
                        }`}></div>
                      </div>
                    </div>
                  </label>
                  <span className="text-xs text-gray-500 mt-1">
                    {settings.encouragingFeedback ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {/* Perform Comprehensive Final Review */}
            <div className="border-b border-gray-200 pb-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 mr-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Perform Comprehensive Final Review
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Enable the Comprehensive Final Review section that provides AI-powered analysis of completed PVQ forms.
                    When disabled, this menu item will be hidden from the navigation.
                  </p>
                  <div className="flex items-center text-xs text-gray-500">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Provides detailed validation and feedback on form completeness
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settings.performComprehensiveReview}
                        onChange={(e) => handleSettingChange('performComprehensiveReview', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`block w-14 h-8 rounded-full transition-colors duration-200 ${
                        settings.performComprehensiveReview ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-200 ${
                          settings.performComprehensiveReview ? 'transform translate-x-6' : ''
                        }`}></div>
                      </div>
                    </div>
                  </label>
                  <span className="text-xs text-gray-500 mt-1">
                    {settings.performComprehensiveReview ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {/* Knowledge Base and RAG Settings */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                FAQ Knowledge Base Settings
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Configure how the FAQ assistance system retrieves and generates responses for security-related questions.
              </p>

              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 mb-4">
                  Choose FAQ Processing Mode
                </h4>

                {/* Knowledge Base Mode */}
                <label className="flex items-start cursor-pointer p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                  <input
                    type="radio"
                    name="faqMode"
                    checked={settings.useKnowledgeBase && !settings.useAdvancedRAG}
                    onChange={() => {
                      const newSettings = {
                        ...settings,
                        useKnowledgeBase: true,
                        useAdvancedRAG: false
                      };
                      setSettings(newSettings);
                      saveSettingsAndNotify(newSettings);
                    }}
                    className="mt-1 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                      </svg>
                      <span className="font-medium text-gray-900">AWS Bedrock Knowledge Base</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Production-ready vector search across uploaded security documents in AWS Bedrock Knowledge Base.
                    </p>
                    <div className="flex items-center text-xs text-gray-500 mt-2">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Best for production environments
                    </div>
                  </div>
                </label>

                {/* Local RAG Mode */}
                <label className="flex items-start cursor-pointer p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                  <input
                    type="radio"
                    name="faqMode"
                    checked={!settings.useKnowledgeBase && settings.useAdvancedRAG}
                    onChange={() => {
                      const newSettings = {
                        ...settings,
                        useKnowledgeBase: false,
                        useAdvancedRAG: true
                      };
                      setSettings(newSettings);
                      saveSettingsAndNotify(newSettings);
                    }}
                    className="mt-1 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="font-medium text-gray-900">Local Advanced RAG</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Local document processing with hybrid search (keyword + semantic + domain-specific) for development and testing.
                    </p>
                    <div className="flex items-center text-xs text-gray-500 mt-2">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Best for development and faster responses
                    </div>
                  </div>
                </label>
              </div>

              {/* Configuration Info Box */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-purple-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <h5 className="text-sm font-medium text-purple-900 mb-2">Current Configuration</h5>
                    <div className="text-sm text-purple-800 space-y-1">
                      {settings.useKnowledgeBase && settings.useAdvancedRAG && (
                        <div>✓ Using AWS Knowledge Base with Advanced RAG features</div>
                      )}
                      {settings.useKnowledgeBase && !settings.useAdvancedRAG && (
                        <div>✓ Using AWS Knowledge Base with standard retrieval</div>
                      )}
                      {!settings.useKnowledgeBase && settings.useAdvancedRAG && (
                        <div>✓ Using Local RAG with advanced processing (recommended for testing)</div>
                      )}
                      {!settings.useKnowledgeBase && !settings.useAdvancedRAG && (
                        <div>⚡ Using basic local processing</div>
                      )}
                      <div className="text-xs text-purple-600 mt-2">
                        FAQ responses will be generated using: {
                          settings.useKnowledgeBase ? 'AWS Bedrock Knowledge Base' : 'Local Document Processing'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Status */}
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-gray-600">
                Settings are automatically saved to your browser's local storage.
              </div>
              <div className="flex items-center">
                {saveStatus === 'saving' && (
                  <div className="flex items-center text-blue-600">
                    <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div className="flex items-center text-green-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Saved
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PVQPOCSettings;
